import type { Accommodation } from "@@/shared/acquire/accommodation";
import type {
    AccommodationFeed,
    AccommodationRateForDateWithCalculatedPrices,
    CalculatedRateLineItem,
    CalculatedRatePrice,
} from "@@/shared/acquire/accommodation-engine/generate-accommodation-feed";
import type { AccommodationQuantities } from "@@/shared/acquire/accommodation-quantity";
import { getAccommodationQuantityGuest } from "@@/shared/acquire/accommodation-quantity";
import {
    getAccommodationRateOptionType,
    isAppliesPerNights,
    type AccommodationRateOptionAppliesTo,
} from "@@/shared/acquire/accommodation-rate-option-applies";
import { asDate } from "@@/shared/date-time/as-date";
import { datesBetweenIso } from "@@/shared/date-time/date-utility";
import { startOfDayZoned } from "@@/shared/date-time/start-and-end-of-day-zoned";
import type { TimeRange } from "@@/shared/date-time/time-range";
import { multiplyPrice } from "@@/shared/prices/multiply-price";
import type { Price } from "@@/shared/prices/price";
import { sumPrices } from "@@/shared/prices/sum-prices";

const getQuantityRateAndPrice = (params: {
    appliesTo: AccommodationRateOptionAppliesTo;
    price: Price;
    quantities: AccommodationQuantities;
}) => {
    const { appliesTo, quantities, price } = params;
    switch (appliesTo) {
        case "ADULT": {
            const totalPrice = multiplyPrice(price, quantities.ADULT);
            return {
                pricePerItem: price,
                totalPrice,
                quantity: quantities.ADULT,
            };
        }

        case "CHILD": {
            const totalPrice = multiplyPrice(price, quantities.CHILD);
            return {
                pricePerItem: price,
                totalPrice,
                quantity: quantities.CHILD,
            };
        }
        case "INFANT": {
            const totalPrice = multiplyPrice(price, quantities.INFANT);
            return {
                pricePerItem: price,
                totalPrice,
                quantity: quantities.INFANT,
            };
        }
        case "GUEST": {
            const totalGuests = getAccommodationQuantityGuest(quantities);

            const totalPrice = multiplyPrice(price, totalGuests);
            return {
                pricePerItem: price,
                totalPrice,
                quantity: totalGuests,
            };
        }
        default:
            return {
                pricePerItem: price,
                totalPrice: price,
                quantity: 1,
            };
    }
};

type EngineResult = {
    included: CalculatedRatePrice[];
};

//Search the feed for the available rates and rooms for the time range
const findEligibleRates = (
    _accommodation: Accommodation,
    accommodationFeed: AccommodationFeed,
    timeRange: TimeRange,
    quantities2: AccommodationQuantities[],
): EngineResult => {
    const startAsDate = startOfDayZoned(asDate(timeRange.start));
    const endAsDate = startOfDayZoned(asDate(timeRange.end));

    // Get all rates for the period
    const partFeed = accommodationFeed.dates.filter(d => {
        const date = asDate(d.date);
        if (startAsDate === endAsDate) {
            return date >= startAsDate && date <= endAsDate;
        }
        return date >= startAsDate && date < endAsDate;
    });

    // //Remove last day as it is not a sleeping night
    // // const partFeed = partFeedWithLastDay; //.slice(0, -1);

    if (partFeed.length === 0)
        return { included: [], error: "NO PARTFEED FOUND" } as EngineResult;

    //Take all specific rates for the period
    const accommodationAndRates = partFeed[0].accommodations;
    const groupRates = accommodationAndRates.flatMap(room => {
        const rates = room.calculatedRates.flatMap(
            ratesWithCalculatedPrices => {
                //Loop through all dates and find rates per night
                //Lookup specific rate for the night that is adjusted

                return quantities2.flatMap(quantities => {
                    const rateLineItems: CalculatedRateLineItem[] =
                        partFeed.flatMap((r, i) => {
                            const rateSpecific = r.accommodations
                                .find(
                                    a =>
                                        a.resourceGroupId ===
                                        room.resourceGroupId,
                                )
                                ?.calculatedRates.find(
                                    rr =>
                                        rr.rateId ===
                                        ratesWithCalculatedPrices.rateId,
                                );
                            //Todo look if these is correct...
                            if (!rateSpecific) return [];

                            return rateSpecific.rateOptionPrices.flatMap(
                                optionPrices => {
                                    const priceData = getQuantityRateAndPrice({
                                        appliesTo: optionPrices.appliesTo,
                                        price: optionPrices.price,
                                        quantities: quantities,
                                    });

                                    const applies =
                                        getAccommodationRateOptionType(
                                            optionPrices.applies,
                                        );
                                    //If PER_STAY then only return the first night
                                    if (applies === "PER_STAY" && i === 0) {
                                        return {
                                            totalPrice: priceData.totalPrice,
                                            option: optionPrices,
                                            night: r.date,
                                            quantity: priceData.quantity,
                                            pricePerItem:
                                                priceData.pricePerItem,
                                        } as CalculatedRateLineItem;
                                    } else if (applies === "PER_NIGHT") {
                                        //If PER_NIGHT then return all nights
                                        return {
                                            totalPrice: priceData.totalPrice,
                                            option: optionPrices,
                                            night: r.date,
                                            quantity: priceData.quantity,
                                            pricePerItem:
                                                priceData.pricePerItem,
                                        } as CalculatedRateLineItem;
                                    } else if (
                                        isAppliesPerNights(optionPrices.applies)
                                    ) {
                                        const nights =
                                            optionPrices.applies.nights;

                                        //Check if the rate is valid for the nights
                                        if (i % nights === 0) {
                                            return {
                                                totalPrice:
                                                    priceData.totalPrice,
                                                option: optionPrices,
                                                night: r.date,
                                                quantity: priceData.quantity,
                                                pricePerItem:
                                                    priceData.pricePerItem,
                                            } as CalculatedRateLineItem;
                                        }
                                    }
                                    return [];
                                },
                            );
                        });

                    const result: CalculatedRatePrice = {
                        quantities,
                        rateWithPrices: ratesWithCalculatedPrices,
                        price: sumPrices(rateLineItems.map(r => r.totalPrice)),
                        rateLineItems,
                    };

                    return result;
                });
            },
        );
        return rates;
    });

    return { included: groupRates } as EngineResult;
};

const isRateValidFor = (params: {
    rate: AccommodationRateForDateWithCalculatedPrices;
    quantities: AccommodationQuantities;
    timeRange: TimeRange;
}) => {
    const { rate, quantities, timeRange } = params;
    const dates = datesBetweenIso(timeRange.start, timeRange.end);
    const nights = dates.length - 1;
    const conditons = rate.conditions ?? [];

    if (conditons.length === 0) return true;

    const valid = conditons.every(c => {
        switch (c.type) {
            case "STAY": {
                if (c.value.min && nights < c.value.min) return false;
                if (c.value.max && nights > c.value.max) return false;
                return true;
            }
            case "GUEST_OCCUPANCY": {
                const totalGuests = getAccommodationQuantityGuest(quantities);
                if (c.value.min && totalGuests < c.value.min) return false;
                if (c.value.max && totalGuests > c.value.max) return false;
                return true;
            }
            default:
                return true;
        }
    });

    return valid;
};

export { findEligibleRates, getQuantityRateAndPrice, isRateValidFor };
